import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const LANGUAGES = [
  { code: 'en', label: 'English' }, { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' }, { code: 'ht', label: 'Haitian Creole' },
  { code: 'vi', label: 'Vietnamese' }, { code: 'ar', label: 'Arabic' },
  { code: 'zh', label: 'Chinese Simplified' }, { code: 'pt', label: 'Portuguese' },
  { code: 'ko', label: 'Korean' }, { code: 'ru', label: 'Russian' },
  { code: 'hi', label: 'Hindi' }, { code: 'tl', label: 'Tagalog' },
];

const LITERACY_LEVELS = [
  { value: 'low', label: 'Simple', description: 'No jargon, everyday words' },
  { value: 'medium', label: 'Standard', description: 'Plain language with brief definitions' },
  { value: 'high', label: 'Detailed', description: 'Standard medical terms explained' },
  { value: 'professional', label: 'Clinical', description: 'Full medical language' },
];

const EDUCATION_LEVELS = [
  { value: '', label: 'Not specified' },
  { value: 'grade-school', label: 'Grade school' },
  { value: 'high-school', label: 'High school' },
  { value: 'college', label: 'College / University' },
  { value: 'graduate', label: 'Graduate / Professional' },
];

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    preferredLanguage: user?.preferredLanguage || 'en',
    healthLiteracyLevel: user?.healthLiteracyLevel || 'medium',
    educationLevel: user?.educationLevel || '',
    culturalContext: user?.culturalContext || '',
    gender: user?.gender || '',
    dateOfBirth: user?.dateOfBirth ? user.dateOfBirth.slice(0, 10) : '',
  });
  const [formError, setFormError] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [deleteStep, setDeleteStep] = useState(0); // 0=hidden, 1=confirm, 2=deleting
  const [deleteError, setDeleteError] = useState('');

  // Re-sync form when user data loads (fixes race condition on fresh page load/refresh)
  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || '',
      preferredLanguage: user.preferredLanguage || 'en',
      healthLiteracyLevel: user.healthLiteracyLevel || 'medium',
      educationLevel: user.educationLevel || '',
      culturalContext: user.culturalContext || '',
      gender: user.gender || '',
      dateOfBirth: user.dateOfBirth ? user.dateOfBirth.slice(0, 10) : '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFormError('');
    if (!form.gender) { setFormError('Gender is required.'); return; }
    if (!form.dateOfBirth) { setFormError('Date of birth is required.'); return; }
    try {
      const res = await api.patch('/users/me', form);
      updateUser(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleteStep(2);
    try {
      await api.delete('/users/me');
      logout();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete account. Please try again.');
      setDeleteStep(1);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Profile & Settings</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Personalize how HealthClear AI explains your results.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 space-y-4">
          <h2 className="font-semibold text-slate-700 dark:text-slate-200">Basic Information</h2>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 text-slate-400 dark:text-slate-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              {GENDERS.map(g => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => handleChange('gender', g.value)}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    form.gender === g.value
                      ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                      : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-sky-300 dark:bg-slate-700'
                  }`}
                >{g.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
            />
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Used to tailor recommendations to your age.</p>
          </div>
        </div>

        {/* Language */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 space-y-4">
          <h2 className="font-semibold text-slate-700 dark:text-slate-200">Language Preference</h2>
          <p className="text-sm text-slate-400 dark:text-slate-500">All your lab explanations will be generated in this language.</p>
          <select
            value={form.preferredLanguage}
            onChange={(e) => handleChange('preferredLanguage', e.target.value)}
            className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
          >
            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>

        {/* Health literacy */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 space-y-4">
          <h2 className="font-semibold text-slate-700 dark:text-slate-200">Health Literacy Level</h2>
          <p className="text-sm text-slate-400 dark:text-slate-500">This controls how technical the explanations are. You can always override this per-upload.</p>
          <div className="grid grid-cols-2 gap-2">
            {LITERACY_LEVELS.map((lvl) => (
              <button
                key={lvl.value}
                type="button"
                onClick={() => handleChange('healthLiteracyLevel', lvl.value)}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  form.healthLiteracyLevel === lvl.value
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                    : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:bg-slate-700'
                }`}
              >
                <p className="font-medium text-sm">{lvl.label}</p>
                <p className="text-xs mt-0.5 opacity-70">{lvl.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Education & cultural context */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 space-y-4">
          <h2 className="font-semibold text-slate-700 dark:text-slate-200">Background (Optional)</h2>
          <p className="text-sm text-slate-400 dark:text-slate-500">Helps us tailor explanations to be most useful to you.</p>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Education Level</label>
            <select
              value={form.educationLevel}
              onChange={(e) => handleChange('educationLevel', e.target.value)}
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
            >
              {EDUCATION_LEVELS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Cultural Context (optional)</label>
            <input
              type="text"
              value={form.culturalContext}
              onChange={(e) => handleChange('culturalContext', e.target.value)}
              placeholder="e.g., Haitian-American, Southeast Asian, Latino/a"
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
            />
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">This helps provide culturally-appropriate framing of sensitive topics.</p>
          </div>
        </div>

        {(error || formError) && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">{formError || error}</p>}

        <button
          type="submit"
          className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {saved ? (
            <><CheckCircleIcon className="w-5 h-5" /> Saved!</>
          ) : 'Save Changes'}
        </button>
      </form>

      {/* Delete Account */}
      <div className="mt-10 bg-white dark:bg-slate-800 border border-red-200 dark:border-red-900/50 rounded-xl p-5">
        <h2 className="font-semibold text-red-700 dark:text-red-400 mb-1">Delete Account</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Permanently delete your account and all associated data, including lab reports and analysis history. This cannot be undone.
        </p>

        {deleteError && (
          <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-2 mb-4">{deleteError}</p>
        )}

        {deleteStep === 0 && (
          <button
            type="button"
            onClick={() => setDeleteStep(1)}
            className="border border-red-400 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400 dark:border-red-700 font-medium text-sm px-4 py-2 rounded-lg transition-colors"
          >
            Delete my account
          </button>
        )}

        {deleteStep === 1 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 space-y-3">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300">
              ⚠️ Are you absolutely sure? This will permanently erase all your data.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleDeleteAccount}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors"
              >
                Yes, delete my account
              </button>
              <button
                type="button"
                onClick={() => { setDeleteStep(0); setDeleteError(''); }}
                className="border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-medium text-sm px-4 py-2 rounded-lg transition-colors hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {deleteStep === 2 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Deleting your account…</p>
        )}
      </div>
    </div>
  );
}
